"use client";

import { Button } from "@pex/ui-web";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const GROUPS_FEATURES = [
  {
    title: "Vital to Meaningful Recovery That Lasts",
    description:
      "Being part of a healthy recovery community is consistently linked with positive, sustainable recovery outcomes.",
    image: {
      src: "/images/find-help/FindHelp_LowerRight01.webp",
      alt: "A top-down view of a recovery group seated in a circle.",
    },
  },
  {
    title: "Spaces of Safety and Belonging",
    description:
      "Recovery groups are non-judgemental environments to be authentic, receive empathy, and experience encouragement.",
    image: {
      src: "/images/find-help/FindHelp_LowerRight02.webp",
      alt: "A group of people sitting together with raised hands during a recovery session.",
    },
  },
  {
    title: "Places of Break-through",
    description:
      "In groups, beliefs and behaviours are transformed, as denial is addressed and members are empowered with new knowledge, insights, and skills.",
    image: {
      src: "/images/find-help/FindHelp_LowerRight03.webp",
      alt: "A person speaking into a microphone, sharing at a recovery group.",
    },
  },
] as const;

export function RecoveryGroupsSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          {/* Left — text + accordion + CTAs */}
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="heading-2 font-bold">
                Recovery Groups are Fundamental
              </h2>
              <p className="body-lg text-muted-foreground">
                Regular recovery groups are essential for anyone seeking
                restoration from an addictive disorder or from the impact of a
                loved one&apos;s addiction
              </p>
            </div>

            <div className="divide-y">
              {GROUPS_FEATURES.map((f, i) => {
                const isActive = activeIndex === i;
                return (
                  <div className="py-5 first:pt-0 last:pb-0" key={f.title}>
                    <button
                      aria-expanded={isActive}
                      className="w-full cursor-pointer text-left"
                      onClick={() => setActiveIndex(i)}
                      type="button"
                    >
                      <h3
                        className={`heading-6 transition-colors duration-200 ${
                          isActive
                            ? "font-bold text-foreground"
                            : "font-semibold text-muted-foreground"
                        }`}
                      >
                        {f.title}
                      </h3>
                    </button>
                    <div
                      className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                      style={{
                        gridTemplateRows: isActive ? "1fr" : "0fr",
                      }}
                    >
                      <div className="overflow-hidden">
                        <p className="body-base pt-2 text-muted-foreground">
                          {f.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                className="w-full sm:w-auto"
                variant="secondary-hybrid"
              >
                <a
                  href={`${process.env.NEXT_PUBLIC_FINDER_URL}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Find a Group
                </a>
              </Button>
              <Button asChild variant="link-icon">
                <Link href="/recovery-groups">
                  Learn More
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right — synchronized image swap */}
          <div className="relative aspect-square overflow-hidden rounded-2xl">
            {GROUPS_FEATURES.map((f, i) => (
              <div
                className="absolute inset-0 transition-opacity duration-500 ease-in-out"
                key={f.image.src}
                style={{ opacity: activeIndex === i ? 1 : 0 }}
              >
                <Image
                  alt={f.image.alt}
                  className="object-cover"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  src={f.image.src}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
